import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { FormData, feelingOptions } from "../types";

interface FeelingStepProps {
  formData: FormData;
  onChange: (field: keyof FormData, value: string) => void;
}

const FeelingStep = ({ formData, onChange }: FeelingStepProps) => {
  const selected = formData.feeling ? formData.feeling.split(",") : [];

  const toggleFeeling = (value: string) => {
    if (value === "other") {
      // Toggle "other" exclusively adds/removes it
      const newSelected = selected.includes("other")
        ? selected.filter((v) => v !== "other")
        : [...selected, "other"];
      onChange("feeling", newSelected.join(","));
      return;
    }
    const newSelected = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange("feeling", newSelected.join(","));
  };

  return (
    <motion.div
      key="feeling"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-primary" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
          Overall Feeling of the Song
        </h2>
        <p className="text-muted-foreground">
          What emotion should the song evoke?
        </p>
      </div>

      <div className="space-y-4">
        <Label>Select one or more *</Label>
        <div className="flex flex-wrap gap-2">
          {feelingOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => toggleFeeling(option.value)}
              className={`px-4 py-2 rounded-full border-2 transition-all ${
                selected.includes(option.value)
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border hover:border-primary/50"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {selected.includes("other") && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="space-y-2"
        >
          <Label htmlFor="feelingOther">Describe the feeling *</Label>
          <Input
            id="feelingOther"
            placeholder="Describe the emotion you want the song to evoke"
            value={formData.feelingOther}
            onChange={(e) => onChange("feelingOther", e.target.value)}
          />
        </motion.div>
      )}
    </motion.div>
  );
};

export default FeelingStep;
